import React from 'react';
import { Headphones, Volume2 } from 'lucide-react';
import { SmallText } from './BrandTypography';

/**
 * AudioTip - A gentle prompt to inform users about audio features
 * 
 * Variants:
 * - 'headphones' - For tools where headphones significantly enhance the experience
 * - 'optional' - For tools where audio is available but optional
 * - 'ambient' - For tools with ambient/background audio
 */
const AudioTip = ({ 
    variant = 'optional', 
    className = '',
    showIcon = true 
}) => {
    const tips = {
        headphones: {
            icon: Headphones,
            text: 'Headphones recommended for the full experience',
            subtext: 'Audio guidance enhances the therapeutic effect',
            bgColor: 'bg-[#1D6B6B]/10',
            textColor: 'text-[#1D6B6B]',
            iconColor: 'text-[#1D6B6B]'
        },
        optional: {
            icon: Volume2,
            text: 'Audio available',
            subtext: 'Use the sound toggle for optional audio guidance',
            bgColor: 'bg-[#7D8E95]/10',
            textColor: 'text-[#7D8E95]',
            iconColor: 'text-[#7D8E95]'
        },
        ambient: {
            icon: Volume2,
            text: 'Optional ambient sound',
            subtext: 'Gentle tones to help you settle — headphones enhance the experience',
            bgColor: 'bg-[#FFBB98]/10',
            textColor: 'text-[#FFBB98]',
            iconColor: 'text-[#FFBB98]'
        },
        meditation: {
            icon: Headphones,
            text: 'Best experienced with headphones',
            subtext: 'Voice-guided meditation with optional background music',
            bgColor: 'bg-[#1D6B6B]/10',
            textColor: 'text-[#1D6B6B]',
            iconColor: 'text-[#1D6B6B]'
        }
    };

    const tip = tips[variant] || tips.optional;
    const Icon = tip.icon;

    return (
        <div 
            className={`flex items-center gap-3 px-4 py-3 rounded-xl ${tip.bgColor} ${className}`}
            data-testid="audio-tip"
        >
            {showIcon && (
                <div className={`flex-shrink-0 ${tip.iconColor}`}>
                    <Icon className="w-5 h-5" />
                </div>
            )}
            <div className="flex-1">
                <SmallText className={`font-medium ${tip.textColor} block`}>
                    {tip.text}
                </SmallText>
                {tip.subtext && (
                    <SmallText className="text-[#7D8E95]/80 text-xs mt-0.5 block">
                        {tip.subtext}
                    </SmallText>
                )}
            </div>
        </div>
    );
};

export default AudioTip;
